import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
    X, Loader2, Paperclip, Camera, Upload, FileText, Image as ImageIcon,
    Trash2, Send, Award, CheckCircle2, Download, Clock,
} from "lucide-react";
import { formatReceivedAt } from "../../lib/dateFormat";

/**
 * V165 — Homework Detail modal.
 *
 * Parent view: question + attachments, compose answer with text +
 * camera/gallery/file, submit / re-submit, see tutor marks.
 *
 * Tutor view: question + own attachments, student submission (text +
 * attachments), evaluate form (marks / marks_out_of / feedback).
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const publicApi = axios.create({ baseURL: API });

const ATTACHMENT_ACCEPT = ".pdf,.png,.jpg,.jpeg,.webp,.heic,.gif,.doc,.docx,.txt,image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const ATTACHMENT_MAX_MB = 10;

export default function HomeworkDetail({ party, token, homeworkId, onClose, onChanged }) {
    const isTutor = party === "tutor";
    const [hw, setHw] = useState(null);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);

    const reload = useCallback(async () => {
        try {
            const r = await publicApi.get(`/portal/${party}/${token}/homeworks/${homeworkId}?_=${Date.now()}`);
            setHw(r.data);
        } catch (_e) {
            toast.error("Could not load homework");
        } finally { setLoading(false); }
    }, [party, token, homeworkId]);

    useEffect(() => { reload(); }, [reload]);

    const uploadFile = async (file, kind) => {
        if (!file || uploading) return;
        if (file.size > ATTACHMENT_MAX_MB * 1024 * 1024) {
            toast.error(`File too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max ${ATTACHMENT_MAX_MB} MB.`);
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const url = kind === "question"
                ? `/portal/tutor/${token}/homeworks/${homeworkId}/attachments`
                : `/portal/parent/${token}/homeworks/${homeworkId}/submissions/attachments`;
            await publicApi.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success(kind === "question" ? "Question file added" : "Answer file uploaded");
            reload();
            onChanged?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const removeAttachment = async (att_id) => {
        try {
            await publicApi.delete(`/portal/${party}/${token}/homeworks/attachments/${att_id}`);
            toast.success("Removed");
            reload();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not remove");
        }
    };

    return (
        <div
            className="fixed inset-0 bg-black/50 z-[90] flex items-start sm:items-center justify-center overflow-y-auto p-0 sm:p-4 classroom-mobile"
            data-testid="homework-detail-modal"
        >
            <div className="bg-white w-full sm:max-w-2xl sm:rounded-2xl shadow-2xl min-h-screen sm:min-h-0 sm:max-h-[92vh] overflow-y-auto">
                <div className="sticky top-0 bg-white border-b border-slate-200 px-4 sm:px-6 py-3 flex items-center justify-between z-10">
                    <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-widest text-cyan-600 font-bold">Homework</div>
                        <div className="text-lg font-heading font-bold text-[#0F172A] truncate">
                            {loading ? "Loading…" : hw?.title || "—"}
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        data-testid="homework-detail-close"
                        data-cm-compact
                        aria-label="Close"
                        className="p-2 hover:bg-slate-100 rounded-lg text-slate-600"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {loading ? (
                    <div className="p-10 text-center text-slate-400">
                        <Loader2 className="w-6 h-6 mx-auto animate-spin" />
                    </div>
                ) : (
                    <div className="p-4 sm:p-6 space-y-5">
                        <QuestionBlock hw={hw} isTutor={isTutor} onUpload={(f) => uploadFile(f, "question")}
                            onRemove={removeAttachment} uploading={uploading} party={party} token={token} />
                        <AnswerBlock
                            hw={hw}
                            isTutor={isTutor}
                            party={party}
                            token={token}
                            uploading={uploading}
                            onUpload={(f) => uploadFile(f, "answer")}
                            onRemove={removeAttachment}
                            onChanged={reload}
                        />
                        {isTutor && <EvaluateBlock hw={hw} token={token} onChanged={() => { reload(); onChanged?.(); }} />}
                        {!isTutor && hw?.submission?.status === "evaluated" && <MarksBlock sub={hw.submission} />}
                    </div>
                )}
            </div>
        </div>
    );
}

// ---------------------------------------------------------- Question block

function QuestionBlock({ hw, isTutor, onUpload, onRemove, uploading, party, token }) {
    const fileRef = useRef(null);
    const attachments = hw?.question_attachments || [];
    const deadline = hw?.deadline;

    return (
        <section>
            <SectionTitle icon={FileText}>Question</SectionTitle>
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2">
                {hw?.description ? (
                    <div className="text-sm text-slate-800 whitespace-pre-wrap">{hw.description}</div>
                ) : (
                    <div className="text-xs text-slate-400 italic">No instructions.</div>
                )}
                {deadline && (
                    <div className="text-[11px] text-slate-500 inline-flex items-center gap-1">
                        <Clock className="w-3 h-3" /> Due {formatReceivedAt(deadline)}
                    </div>
                )}
                {attachments.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                        {attachments.map((a) => (
                            <AttachmentPreview
                                key={a.id}
                                att={a}
                                party={party}
                                token={token}
                                onRemove={isTutor ? () => onRemove(a.id) : null}
                            />
                        ))}
                    </div>
                )}
                {isTutor && (
                    <div className="pt-2">
                        <button
                            type="button"
                            onClick={() => fileRef.current?.click()}
                            disabled={uploading}
                            data-testid="homework-question-attach"
                            className="inline-flex items-center gap-1.5 text-xs font-semibold bg-white border border-slate-200 text-slate-700 hover:border-cyan-300 hover:text-cyan-800 px-3 py-1.5 rounded-lg disabled:opacity-60"
                        >
                            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
                            Attach question file
                        </button>
                        <input
                            ref={fileRef}
                            type="file"
                            accept={ATTACHMENT_ACCEPT}
                            className="hidden"
                            data-testid="homework-question-attach-input"
                            onChange={(e) => {
                                const f = e.target.files?.[0];
                                e.target.value = "";
                                if (f) onUpload(f);
                            }}
                        />
                    </div>
                )}
            </div>
        </section>
    );
}

// ---------------------------------------------------------- Answer block

function AnswerBlock({ hw, isTutor, party, token, uploading, onUpload, onRemove, onChanged }) {
    const sub = hw?.submission;
    const attachments = sub?.answer_attachments || [];
    const evaluated = sub?.status === "evaluated";

    // Parent-only local state for the answer text composer
    const [text, setText] = useState(sub?.answer_text || "");
    const [saving, setSaving] = useState(false);
    const galleryRef = useRef(null);
    const cameraRef = useRef(null);

    useEffect(() => { setText(sub?.answer_text || ""); }, [sub?.id, sub?.answer_text]);

    const submit = async () => {
        setSaving(true);
        try {
            await publicApi.post(`/portal/parent/${token}/homeworks/${hw.id}/submissions`, {
                answer_text: text.trim() || null,
            });
            toast.success(sub ? "Answer updated" : "Answer submitted");
            onChanged?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not submit");
        } finally {
            setSaving(false);
        }
    };

    return (
        <section>
            <SectionTitle icon={Upload}>{isTutor ? "Student's Answer" : "Your Answer"}</SectionTitle>

            {isTutor ? (
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2" data-testid="homework-tutor-answer-view">
                    {!sub ? (
                        <div className="text-xs text-slate-500 italic">Student hasn't submitted yet.</div>
                    ) : (
                        <>
                            {sub.answer_text ? (
                                <div className="text-sm text-slate-800 whitespace-pre-wrap">{sub.answer_text}</div>
                            ) : (
                                <div className="text-xs text-slate-400 italic">No written answer — see attachments below.</div>
                            )}
                            <div className="text-[11px] text-slate-500">
                                Submitted {formatReceivedAt(sub.submitted_at)}
                                {sub.resubmitted_at && <> · Re-submitted {formatReceivedAt(sub.resubmitted_at)}</>}
                            </div>
                            {attachments.length > 0 && (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                                    {attachments.map((a) => (
                                        <AttachmentPreview key={a.id} att={a} party={party} token={token} onRemove={null} />
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            ) : (
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-3" data-testid="homework-parent-answer-view">
                    <textarea
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        placeholder="Type your answer here, or upload a photo below…"
                        data-testid="homework-answer-text"
                        rows={4}
                        maxLength={6000}
                        disabled={evaluated}
                        className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-200 outline-none resize-none disabled:bg-slate-100"
                    />
                    {attachments.length > 0 && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {attachments.map((a) => (
                                <AttachmentPreview
                                    key={a.id}
                                    att={a}
                                    party={party}
                                    token={token}
                                    onRemove={evaluated ? null : () => onRemove(a.id)}
                                />
                            ))}
                        </div>
                    )}
                    {evaluated ? (
                        <div className="text-[11px] text-emerald-700 inline-flex items-center gap-1">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Marked by your tutor · answer is locked.
                        </div>
                    ) : (
                        <div className="flex flex-wrap gap-2">
                            <button
                                type="button"
                                onClick={() => cameraRef.current?.click()}
                                disabled={uploading}
                                data-testid="homework-answer-camera"
                                data-cm-primary
                                className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-800 hover:border-cyan-400 hover:text-cyan-800 px-3 py-2 rounded-lg text-sm font-semibold disabled:opacity-60"
                            >
                                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                                Snap answer
                            </button>
                            <button
                                type="button"
                                onClick={() => galleryRef.current?.click()}
                                disabled={uploading}
                                data-testid="homework-answer-file"
                                data-cm-primary
                                className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-800 hover:border-cyan-400 hover:text-cyan-800 px-3 py-2 rounded-lg text-sm font-semibold disabled:opacity-60"
                            >
                                <Paperclip className="w-4 h-4" />
                                Upload file
                            </button>
                            <input
                                ref={cameraRef}
                                type="file"
                                accept="image/*"
                                capture="environment"
                                className="hidden"
                                data-testid="homework-answer-camera-input"
                                onChange={(e) => {
                                    const f = e.target.files?.[0];
                                    e.target.value = "";
                                    if (f) onUpload(f);
                                }}
                            />
                            <input
                                ref={galleryRef}
                                type="file"
                                accept={ATTACHMENT_ACCEPT}
                                className="hidden"
                                data-testid="homework-answer-file-input"
                                onChange={(e) => {
                                    const f = e.target.files?.[0];
                                    e.target.value = "";
                                    if (f) onUpload(f);
                                }}
                            />
                            <button
                                type="button"
                                onClick={submit}
                                disabled={saving || (!text.trim() && attachments.length === 0)}
                                data-testid="homework-answer-submit"
                                data-cm-primary
                                className="ml-auto inline-flex items-center gap-1.5 bg-cyan-600 hover:bg-cyan-700 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-60"
                            >
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                {sub ? "Re-submit" : "Submit answer"}
                            </button>
                        </div>
                    )}
                    {sub && !evaluated && (
                        <div className="text-[11px] text-cyan-800 inline-flex items-center gap-1" data-testid="homework-answer-submitted-pill">
                            <CheckCircle2 className="w-3 h-3" /> Submitted {formatReceivedAt(sub.submitted_at)} · waiting for tutor to mark.
                        </div>
                    )}
                </div>
            )}
        </section>
    );
}

// ---------------------------------------------------------- Evaluate block (tutor)

function EvaluateBlock({ hw, token, onChanged }) {
    const sub = hw?.submission;
    const [marks, setMarks] = useState(sub?.marks ?? "");
    const [outOf, setOutOf] = useState(sub?.marks_out_of ?? 10);
    const [feedback, setFeedback] = useState(sub?.feedback ?? "");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setMarks(sub?.marks ?? "");
        setOutOf(sub?.marks_out_of ?? 10);
        setFeedback(sub?.feedback ?? "");
    }, [sub?.id, sub?.marks, sub?.marks_out_of, sub?.feedback]);

    if (!sub) return null;

    const submit = async () => {
        setSaving(true);
        try {
            await publicApi.post(`/portal/tutor/${token}/homeworks/${hw.id}/evaluate`, {
                marks: marks === "" ? null : Number(marks),
                marks_out_of: outOf === "" ? null : Number(outOf),
                feedback: feedback.trim() || null,
            });
            toast.success("Grade saved");
            onChanged?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not save grade");
        } finally { setSaving(false); }
    };

    return (
        <section>
            <SectionTitle icon={Award}>Evaluate</SectionTitle>
            <div className="bg-emerald-50/40 border border-emerald-200 rounded-xl p-3 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                    <label className="block">
                        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Marks</div>
                        <input
                            type="number"
                            inputMode="decimal"
                            min={0}
                            step="0.5"
                            value={marks}
                            onChange={(e) => setMarks(e.target.value)}
                            data-testid="homework-eval-marks"
                            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:border-emerald-400 focus:ring-1 focus:ring-emerald-200 outline-none"
                        />
                    </label>
                    <label className="block">
                        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Out of</div>
                        <input
                            type="number"
                            inputMode="decimal"
                            min={0}
                            step="0.5"
                            value={outOf}
                            onChange={(e) => setOutOf(e.target.value)}
                            data-testid="homework-eval-out-of"
                            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:border-emerald-400 focus:ring-1 focus:ring-emerald-200 outline-none"
                        />
                    </label>
                </div>
                <label className="block">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Feedback for the student</div>
                    <textarea
                        rows={3}
                        maxLength={4000}
                        value={feedback}
                        onChange={(e) => setFeedback(e.target.value)}
                        data-testid="homework-eval-feedback"
                        placeholder="What went well, what to improve, next steps…"
                        className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:border-emerald-400 focus:ring-1 focus:ring-emerald-200 outline-none resize-none"
                    />
                </label>
                <div className="flex justify-end">
                    <button
                        type="button"
                        onClick={submit}
                        disabled={saving}
                        data-testid="homework-eval-submit"
                        data-cm-primary
                        className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-60"
                    >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Award className="w-4 h-4" />}
                        {sub.status === "evaluated" ? "Update grade" : "Save grade"}
                    </button>
                </div>
                {sub.status === "evaluated" && (
                    <div className="text-[11px] text-emerald-700">
                        Previously marked {formatReceivedAt(sub.evaluated_at)}.
                    </div>
                )}
            </div>
        </section>
    );
}

// ---------------------------------------------------------- Marks display (parent)

function MarksBlock({ sub }) {
    const percent = sub?.marks_out_of ? Math.round((sub.marks / sub.marks_out_of) * 100) : null;
    return (
        <section data-testid="homework-marks-block">
            <SectionTitle icon={Award}>Your Marks</SectionTitle>
            <div className="bg-gradient-to-br from-emerald-50 to-cyan-50 border-2 border-emerald-300 rounded-xl p-4">
                <div className="flex items-baseline gap-2">
                    <div className="text-3xl font-heading font-bold text-emerald-800">{sub.marks ?? "—"}</div>
                    {sub.marks_out_of && <div className="text-lg text-emerald-700">/ {sub.marks_out_of}</div>}
                    {percent !== null && <div className="ml-auto text-sm font-bold text-emerald-700">{percent}%</div>}
                </div>
                {sub.feedback && (
                    <div className="mt-3 pt-3 border-t border-emerald-200/70">
                        <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold mb-1">Feedback</div>
                        <div className="text-sm text-slate-800 whitespace-pre-wrap">{sub.feedback}</div>
                    </div>
                )}
                <div className="text-[11px] text-emerald-700 mt-2">
                    Marked {formatReceivedAt(sub.evaluated_at)}
                </div>
            </div>
        </section>
    );
}

// ---------------------------------------------------------- Attachment preview

function AttachmentPreview({ att, party, token, onRemove }) {
    const isImage = (att.content_type || "").startsWith("image/");
    const src = `${API}/portal/${party}/${token}/homeworks/attachments/${att.id}`;
    return (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid={`homework-attachment-${att.id}`}>
            {isImage ? (
                <a href={src} target="_blank" rel="noreferrer" className="block bg-slate-100">
                    <img
                        src={src}
                        alt={att.original_filename}
                        className="w-full max-h-40 object-cover"
                        loading="lazy"
                    />
                </a>
            ) : (
                <a href={src} target="_blank" rel="noreferrer" className="flex items-center gap-2 p-3 hover:bg-slate-50">
                    <div className="w-9 h-9 rounded bg-rose-50 text-rose-700 flex items-center justify-center shrink-0">
                        <FileText className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="text-xs font-semibold text-slate-800 truncate">{att.original_filename}</div>
                        <div className="text-[10px] text-slate-500">{att.content_type} · {fmtSize(att.size)}</div>
                    </div>
                    <Download className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                </a>
            )}
            <div className="flex items-center justify-between px-2 py-1 border-t border-slate-100">
                <div className="text-[10px] text-slate-500 truncate">{isImage ? att.original_filename : ""}</div>
                {onRemove && (
                    <button
                        type="button"
                        onClick={onRemove}
                        data-testid={`homework-attachment-remove-${att.id}`}
                        data-cm-compact
                        aria-label="Remove attachment"
                        className="text-rose-600 hover:text-rose-800 p-1 rounded"
                    >
                        <Trash2 className="w-3.5 h-3.5" />
                    </button>
                )}
            </div>
        </div>
    );
}

function SectionTitle({ icon: Icon, children }) {
    return (
        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-2 inline-flex items-center gap-1.5">
            <Icon className="w-3 h-3" /> {children}
        </div>
    );
}

function fmtSize(bytes) {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
